import { useState } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import {
  bulkCheckProductCodes,
  bulkAddProducts,
} from "../../services/services";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { useNavigate } from "react-router";

const BulkUploadItems = () => {
  const [csvData, setCsvData] = useState([]);
  const [duplicateCodes, setDuplicateCodes] = useState([]);
  const [savedCodes, setSavedCodes] = useState([]);
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [save, setSave] = useState(false);
  const navigate = useNavigate();

  // Headers
  const headers = [
    "name",
    "productCode",
    "category",
    "hsnCode",
    "description",
    "minStockAlert",
    "salePrice",
    "gstRate",
    "quantity",
    "purchasePrice",
    "location",
    "discount",
    "date",
  ];

  // Download Sample CSV
  const downloadSampleCSV = () => {
    const sampleRow = [
      "Sample Product",
      "ITEM001",
      "1234",
      "Sample description",
      "5",
      "100",
      "18",
      "50",
      "80",
      "Bhopal Store",
      "10",
      "2026-02-11",
    ];

    const csvContent = headers.join(",") + "\n" + sampleRow.join(",");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sample_products.csv";
    link.click();
  };

  // Drag & Drop Handlers
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload({ target: { files: [file] } });
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = () => setIsDragging(true);
  const handleDragLeave = () => setIsDragging(false);

  // Upload CSV
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "text/csv") {
      toast.error("Only CSV files allowed!");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvData(results.data);
        setShowUploadInfo(true);
        toast.success("CSV loaded successfully");
      },
    });
  };

  // Submit
  const handleSubmit = async () => {
    setSave(true)
    try {
      const codes = csvData.map((p) => p.productCode);
      const res = await bulkCheckProductCodes({ codes });

      const duplicates = (res.data.duplicates || []).map((d) =>
        typeof d === "string" ? d.trim() : d.productCode.trim(),
      );

      setDuplicateCodes(duplicates);

      const validProducts = csvData.filter(
        (p) => !duplicates.includes(p.productCode?.trim()),
      );

      if (!validProducts.length) {
        toast.error("All products already exist");
        return;
      }

      await bulkAddProducts(validProducts);
      setSavedCodes(validProducts.map((p) => p.productCode.trim()));
      toast.success(`${validProducts.length} Products uploaded successfully`);
      navigate("/items")
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setSave(false)
    }
  };

  return (
    <div id="main" className="p-3">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 custom-title">Bulk Upload Product</h4>

        <button className="btn btn-primary" onClick={downloadSampleCSV}>
          <i className="bi bi-download"></i>

          {/* Hide text on mobile */}
          <span className="d-none d-md-inline ms-1">Sample CSV</span>
        </button>
      </div>

      {/* UPLOAD UI */}
      <div className="row g-4 mt-4 px-3">
        <div className="col-md-4">
          <label
            htmlFor="csvInput"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className="border-dashed p-5 rounded text-center d-flex justify-content-center flex-column"
            style={{
              cursor: "pointer",
              background: isDragging ? "#E3F2FD" : "#F8F9FA",
              border: isDragging ? "2px dashed #0d6efd" : "2px dashed #b4b4b4",
              height: "20vh",
              transition: "0.2s",
            }}
          >
            <h5>
              <i className="bi bi-upload"></i> Upload CSV File
            </h5>
            <p className="text-muted">
              {isDragging ? "Drop file here" : "Click CSV file"}
            </p>
          </label>

          <input
            id="csvInput"
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileUpload}
          />
        </div>
      </div>

      {/* INFO MESSAGE */}
      {showUploadInfo && (
        <div className="alert alert-warning mt-3">
          {/* ⚠️ Please download the <b>Sample CSV file</b> and do not change column
          headings while uploading data. */}
          Download <b>Sample CSV file</b> and keep column headings unchanged
          while uploading.
        </div>
      )}

      {/* PREVIEW TABLE */}
      {csvData.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <h5>Preview Products ({csvData.length})</h5>
          </div>

          <div className="table-responsive mt-3">
            <table className="table table-bordered text-nowrap">
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Item Code</th>
                  <th>HSN</th>
                  <th>Purchase</th>
                  <th>Sale</th>
                  <th>Stock</th>
                  <th>Discount</th>
                  <th>Location</th>
                  <th>Min Stock</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {csvData.map((item, index) => {
                  const code = item.productCode?.trim();
                  const isDuplicate = duplicateCodes.includes(code);
                  const isSaved = savedCodes.includes(code);

                  return (
                    <tr
                      key={index}
                    //   className={
                    //     isDuplicate
                    //       ? "table-danger"
                    //       : isSaved
                    //         ? "table-success"
                    //         : ""
                    //   }
                    >
                      <td>{item.name}</td>
                      <td>{item.productCode}</td>
                      <td>{item.hsnCode}</td>
                      <td>{item.purchasePrice}</td>
                      <td>{item.salePrice}</td>
                      <td>{item.quantity}</td>
                      <td>{item.discount}</td>
                      <td>{item.location}</td>
                      <td>{item.minStockAlert}</td>
                      <td>{item.date}</td>
                      <td>
                        {isDuplicate && (
                          <span className="text-danger fw-bold">
                            Already Exists
                          </span>
                        )}
                        {isSaved && (
                          <span className="text-success fw-bold">Saved</span>
                        )}
                        {!isDuplicate && !isSaved && (
                          <span className="text-muted">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-primary" onClick={handleSubmit} disabled={save}>
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default headerLayoutWrapper(BulkUploadItems);
